import type { AgentPhase } from "@/types/createAgent";
import type { CreateFormData } from "@/components/create/types";

/**
 * Phase is driven by image state first. Simple model:
 * 0: no image yet (initial, front_design)
 * 1: front image generated (front_review) - show Get free quote, Design the back
 * 2: back image generated (back_review)
 * 3: quote requested (finalization) - when user explicitly skipped back or submitted
 */
const FRONT_DESIGN_FIELDS = [
  "color",
  "length",
  "sleeve",
  "neckline",
  "silhouette",
  "fabric",
] as const;

function countFrontDesignFields(data: Partial<CreateFormData>): number {
  return FRONT_DESIGN_FIELDS.filter(
    field => data[field as keyof CreateFormData]
  ).length;
}

export function detectPhase(
  collectedData: Partial<CreateFormData>,
  options: { hasFrontImage?: boolean; hasBackImage?: boolean } = {}
): AgentPhase {
  const hasPrompt = !!collectedData.prompt?.trim();
  const hasOccasion = !!collectedData.preset_type;
  const frontFieldsCount = countFrontDesignFields(collectedData);

  // Phase 0: No image yet - initial or front_design
  if (!options.hasFrontImage && !options.hasBackImage) {
    if (!hasPrompt && !(hasOccasion && frontFieldsCount >= 2)) {
      return "initial";
    }
    return "front_design";
  }

  // Phase 2: Back image exists
  if (options.hasBackImage) {
    return "back_review";
  }

  // Phase 1: Front image exists, no back image
  if (options.hasFrontImage) {
    // User is describing the back (clicked "Design the back") → back_design
    if (
      collectedData.backPrompt?.trim() ||
      collectedData.generateBack === true
    ) {
      return "back_design";
    }
    // Otherwise → front_review (show "Get free quote", "Design the back")
    return "front_review";
  }

  // Fallback (should not reach)
  return "front_design";
}

const VALID_PHASES: AgentPhase[] = [
  "initial",
  "front_design",
  "front_review",
  "back_design",
  "back_review",
  "finalization",
];

export function normalizePhase(
  phase: string | null | undefined,
  fallback: AgentPhase
): AgentPhase {
  if (!phase) return fallback;
  return VALID_PHASES.includes(phase as AgentPhase)
    ? (phase as AgentPhase)
    : fallback;
}

export function resolveAgentPhase(params: {
  requestedPhase: string | null | undefined;
  fallbackPhase: AgentPhase;
  formState: Partial<CreateFormData>;
  hasFrontImage?: boolean;
  hasBackImage?: boolean;
}): AgentPhase {
  const normalizedRequested = normalizePhase(
    params.requestedPhase,
    params.fallbackPhase
  );

  const detected = detectPhase(params.formState, {
    hasFrontImage: params.hasFrontImage,
    hasBackImage: params.hasBackImage,
  });

  // Submission is an explicit terminal transition. Image-derived review phases
  // should not pull a quote request back into review after submit_quote runs.
  if (normalizedRequested === "finalization") {
    return "finalization";
  }

  // Trust explicit valid transitions from the agent, but never go outside
  // the deterministic phase graph implied by available images and form state.
  if (
    (detected === "back_review" && normalizedRequested !== "back_review") ||
    (detected === "front_review" &&
      normalizedRequested === "initial" &&
      params.hasFrontImage)
  ) {
    return detected;
  }

  return normalizedRequested;
}
